import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface MissingFile {
  readonly kind: string;
  readonly id: string;
  readonly label: string | null;
  readonly reference: string | null;
  readonly recordedAt: string | null;
  readonly hash: string | null;
}

interface RecoveryResult {
  readonly fileName: string;
  readonly outcome: 'RESTORED' | 'NO_MATCH' | 'NOT_MISSING';
  readonly records: ReadonlyArray<{ kind: string; id: string; reference: string | null; label: string | null }>;
}

/**
 * Administration > Stored File Recovery. The records of this tenant whose
 * stored file is missing, and putting files back: each chosen file is
 * accepted only for the records whose recorded SHA-256 it matches, so only
 * the original can stand in for a lost file.
 */
export function StoredFileRecoveryPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const [missing, setMissing] = useState<readonly MissingFile[] | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  // Bumped after each upload so the file picker is emptied with the list.
  const [pickerKey, setPickerKey] = useState(0);
  const [results, setResults] = useState<readonly RecoveryResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setMissing(null);
    try {
      const r = await apiRequest<{ data: MissingFile[] }>('/v1/stored-files/missing', { token });
      setMissing(r.data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403 ? t('m23.recovery.forbidden') : (e as Error).message);
      setMissing([]);
    }
  }, [token, t]);

  useEffect(() => { void load(); }, [load]);

  const recover = async (): Promise<void> => {
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    setResults(null);
    try {
      const body = new FormData();
      for (const f of files) body.append('files', f);
      const r = await apiRequest<{ data: RecoveryResult[] }>('/v1/stored-files/recover', {
        method: 'POST', token, body,
      });
      setResults(r.data);
      setFiles([]);
      setPickerKey((k) => k + 1);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="stored-file-recovery">
      <h1>{t('m23.recovery.title')}</h1>
      <p>{t('m23.recovery.intro')}</p>
      {error && <p role="alert" aria-label="recovery-error">{error}</p>}

      <section aria-label="recovery-upload">
        <label htmlFor="recovery-files">{t('m23.recovery.choose')}</label>
        <input key={pickerKey} id="recovery-files" type="file" multiple disabled={busy}
               onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
        <Button type="button" variant="primary" aria-label="recover-files"
                disabled={busy || files.length === 0} onClick={() => void recover()}>
          {busy ? t('m23.recovery.working') : t('m23.recovery.put', { count: files.length })}
        </Button>
      </section>

      {results && (
        <section aria-label="recovery-results">
          <h2>{t('m23.recovery.results')}</h2>
          <ul>
            {results.map((r) => (
              <li key={r.fileName} aria-label={`result-${r.fileName}`}>
                {r.fileName}: {t(`m23.recovery.outcome.${r.outcome}`, { count: r.records.length })}
                {r.records.length > 0 && ` (${r.records.map((x) => `${t(`m23.recovery.kind.${x.kind}`)} ${x.reference ?? ''} ${x.label ?? ''}`.trim()).join('; ')})`}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="missing-files">
        <h2>{t('m23.recovery.missing', { count: missing?.length ?? 0 })}</h2>
        {missing === null ? (
          <p>{t('common.loading')}</p>
        ) : missing.length === 0 ? (
          <p aria-label="nothing-missing">{t('m23.recovery.none')}</p>
        ) : (
          <table aria-label="missing-table">
            <thead>
              <tr>
                <th scope="col">{t('m23.recovery.col.kind')}</th>
                <th scope="col">{t('m23.recovery.col.reference')}</th>
                <th scope="col">{t('m23.recovery.col.what')}</th>
                <th scope="col">{t('m23.recovery.col.recorded')}</th>
                <th scope="col">{t('m23.recovery.col.fingerprint')}</th>
              </tr>
            </thead>
            <tbody>
              {missing.map((m) => (
                <tr key={`${m.kind}-${m.id}`} aria-label={`missing-${m.id}`}>
                  <td>{t(`m23.recovery.kind.${m.kind}`)}</td>
                  <td>{m.reference ?? '—'}</td>
                  <td>{m.label ?? '—'}</td>
                  <td>{m.recordedAt ? at(m.recordedAt) : '—'}</td>
                  <td title={m.hash ?? ''}>{m.hash ? `${m.hash.slice(0, 12)}…` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
